// ================================
// CHAT THREAD
// The actual conversation UI — reused for driver<->customer (order-scoped)
// and admin<->member/driver (standing) threads. Telegram/push only ever
// deliver a "new message" nudge; this is where the real conversation lives.
// ================================
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSocket } from '../../context/SocketContext';

export default function ChatThread({ fetchUrl, postUrl, title, selfRole, onClose }) {
  const socket = useSocket();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { fetchMessages(); }, [fetchUrl]);

  useEffect(() => {
    if (!socket) return;
    function onNew(payload) {
      // Both thread shapes include enough to just refetch — simplest way to
      // stay correct without juggling multiple matching schemes client-side.
      fetchMessages();
    }
    socket.on('message:new', onNew);
    return () => socket.off('message:new', onNew);
  }, [socket, fetchUrl]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function fetchMessages() {
    try {
      const res = await axios.get(fetchUrl);
      setMessages(res.data.messages || []);
    } catch (e) {}
    finally { setLoading(false); }
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    const body = text.trim();
    setText('');
    try {
      await axios.post(postUrl, { text: body });
      fetchMessages();
    } catch (e) {
      setText(body); // put it back so they don't lose what they typed
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 460, maxHeight: '80vh', background: 'var(--bg-card, #06091a)',
        border: '1px solid var(--border-hover, #1e3a6a)', borderTopLeftRadius: 20, borderTopRightRadius: 20,
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid var(--border, #0a1628)'
        }}>
          <p style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 16, letterSpacing: 1, color: 'var(--text-primary, #e8e8e8)' }}>{title}</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #3a5a8a)', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200 }}>
          {loading && <p style={{ fontSize: 12, color: 'var(--text-secondary, #3a5a8a)', textAlign: 'center' }}>Loading…</p>}
          {!loading && messages.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary, #3a5a8a)', textAlign: 'center', marginTop: 20 }}>No messages yet — say hello.</p>
          )}
          {messages.map((m, i) => {
            const mine = m.from === selfRole;
            return (
              <div key={i} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%', padding: '8px 12px', borderRadius: 14,
                  background: mine ? 'var(--accent-blue, #1e6bff)' : 'var(--border, #0a1628)',
                  color: mine ? '#fff' : 'var(--text-primary, #e8e8e8)',
                  fontSize: 13.5, wordBreak: 'break-word'
                }}>
                  {m.text}
                  <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 3 }}>
                    {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--border, #0a1628)' }}>
          <input
            type="text" value={text} onChange={e => setText(e.target.value)}
            placeholder="Type a message…" autoFocus
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 20,
              background: 'var(--border, #0a1628)', border: '1px solid var(--border-mid, #0d1e3a)',
              color: 'var(--text-primary, #e8e8e8)', fontSize: 13.5, outline: 'none'
            }}
          />
          <button type="submit" disabled={sending || !text.trim()} style={{
            padding: '0 18px', borderRadius: 20, border: 'none',
            background: 'var(--accent-blue, #1e6bff)', color: '#fff', fontSize: 13, cursor: 'pointer',
            opacity: (sending || !text.trim()) ? 0.6 : 1
          }}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
